import { useCallback } from 'react'
import type { Dispatch, SetStateAction, RefObject } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Flex,
  useColorMode,
} from '@chakra-ui/react'
import { toast } from 'sonner'
import { buildFolderTree } from '@/lib/utils/folder-tree'
import { type ShareSummary } from '@/lib/api/keys'
import { updateShareMetadata } from '@/lib/api/shares'
import { ShareItem } from './ShareItem'
import { FolderRow } from './FolderRow'

interface PublishedSharesSectionProps {
  activeShares: ShareSummary[]
  selectedFileId?: string
  onSelectFile: (shareId: string) => void
  onCloneShare?: (shareId: string) => void
  // Account helpers
  apiKey: string | null | undefined
  loadShares: () => void
  handleCopyLink: (share: ShareSummary) => void
  handleRevoke: (shareId: string) => void
  handleViewDetails: (share: ShareSummary) => void
  isCopied: (shareId: string) => boolean
  getShareUrl: (share: ShareSummary, mode?: 'edit') => string
  getDisplayName: (share: ShareSummary) => string
  isEncrypted: (share: ShareSummary) => boolean
  // Folder tree
  preservedFolders: Set<string>
  setPreservedFolders: Dispatch<SetStateAction<Set<string>>>
  expandedFolders: Set<string>
  toggleFolder: (path: string) => void
  deletePreservedFolder: (path: string) => void
  // Drag-and-drop
  draggingShareId: string | null
  setDraggingShareId: Dispatch<SetStateAction<string | null>>
  dragOverFolderPath: string | null
  setDragOverFolderPath: Dispatch<SetStateAction<string | null>>
  dragImageRef: RefObject<HTMLDivElement | null>
  handleDragEnd: () => void
  // Layout
  hasDrafts: boolean
  handleSectionMouseDown: (e: React.MouseEvent) => void
}

export function PublishedSharesSection({
  activeShares,
  selectedFileId,
  onSelectFile,
  onCloneShare,
  apiKey,
  loadShares,
  handleCopyLink,
  handleRevoke,
  handleViewDetails,
  isCopied,
  getShareUrl,
  getDisplayName,
  isEncrypted,
  preservedFolders,
  setPreservedFolders,
  expandedFolders,
  toggleFolder,
  deletePreservedFolder,
  draggingShareId,
  setDraggingShareId,
  dragOverFolderPath,
  setDragOverFolderPath,
  dragImageRef,
  handleDragEnd,
  hasDrafts,
  handleSectionMouseDown,
}: PublishedSharesSectionProps) {
  const { colorMode } = useColorMode()

  // Handle folder drop — shared by FolderRow and root drop target
  const handleFolderDrop = useCallback(
    (targetPath: string, e: React.DragEvent) => {
      e.preventDefault()
      setDragOverFolderPath(null)
      try {
        const data = JSON.parse(e.dataTransfer.getData('text/plain')) as {
          type: string
          id: string
          sourceFolder: string
        }
        if (data.type === 'share' && apiKey) {
          if (
            data.sourceFolder &&
            data.sourceFolder !== '/' &&
            data.sourceFolder !== targetPath
          ) {
            setPreservedFolders(
              (prev) => new Set([...prev, data.sourceFolder]),
            )
          }
          updateShareMetadata(apiKey, data.id, { folderPath: targetPath })
            .then(() => loadShares())
            .catch(() => toast.error('Failed to move share'))
        }
      } catch {
        /* ignore invalid drag data */
      }
    },
    [apiKey, loadShares, setDragOverFolderPath, setPreservedFolders],
  )

  if (activeShares.length === 0) return null

  const root = buildFolderTree(activeShares, preservedFolders)
  const hasSubfolders = root.children.size > 0

  // Props shared by ShareItem and FolderRow
  const shareItemProps = {
    selectedFileId,
    onSelectFile,
    onCloneShare,
    getDisplayName,
    isEncrypted,
    getShareUrl,
    handleCopyLink,
    handleRevoke,
    handleViewDetails,
    isCopied,
    draggingShareId,
    setDraggingShareId,
    dragImageRef,
    handleDragEnd,
  }

  const folderProps = {
    expandedFolders,
    toggleFolder,
    preservedFolders,
    deletePreservedFolder,
    dragOverFolderPath,
    setDragOverFolderPath,
    onFolderDrop: handleFolderDrop,
    ...shareItemProps,
  }

  return (
    <>
      {/* Section resize handle - only when drafts section is visible */}
      {hasDrafts && (
        <Box
          h="3px"
          minH="3px"
          bg={colorMode === 'dark' ? 'stone.700' : 'stone.200'}
          cursor="row-resize"
          onMouseDown={handleSectionMouseDown}
          transition="all 0.15s"
          flexShrink={0}
          _hover={{
            h: '5px',
            minH: '5px',
            bg: colorMode === 'dark' ? 'stone.600' : 'stone.400',
          }}
          _active={{
            h: '5px',
            minH: '5px',
            bg: 'brand.500',
          }}
        />
      )}

      {/* Published section */}
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        minH={0}
        overflow="hidden"
      >
        <Flex
          align="center"
          justify="space-between"
          px={3}
          pt={2}
          pb={1}
          flexShrink={0}
        >
          <Text
            fontSize="xs"
            fontWeight="semibold"
            textTransform="uppercase"
            color="stone.500"
          >
            Published ({activeShares.length})
          </Text>
        </Flex>

        {/* Published shares list - grouped by folder */}
        <VStack
          flex={1}
          spacing={0}
          align="stretch"
          overflow="auto"
          px={2}
          pt={0}
          pb={2}
          minH={0}
        >
          {!hasSubfolders ? (
            root.shares.map((share) => (
              <ShareItem
                key={share.id}
                share={share}
                depth={0}
                {...shareItemProps}
              />
            ))
          ) : (
            <>
              {/* Root drop target */}
              <HStack
                pr={2}
                py={1}
                pl={2}
                borderRadius="md"
                cursor="default"
                opacity={draggingShareId ? 1 : 0.5}
                transition="all 0.15s ease"
                onDragOver={(e) => {
                  e.preventDefault()
                  e.dataTransfer.dropEffect = 'move'
                  setDragOverFolderPath('/')
                }}
                onDragLeave={() => {
                  setDragOverFolderPath((prev) =>
                    prev === '/' ? null : prev,
                  )
                }}
                onDrop={(e) => handleFolderDrop('/', e)}
                bg={
                  dragOverFolderPath === '/'
                    ? colorMode === 'dark'
                      ? 'whiteAlpha.100'
                      : 'blackAlpha.50'
                    : undefined
                }
                outline={dragOverFolderPath === '/' ? '2px solid' : 'none'}
                outlineColor={
                  dragOverFolderPath === '/'
                    ? colorMode === 'dark'
                      ? 'brand.400'
                      : 'brand.600'
                    : 'transparent'
                }
                outlineOffset="-2px"
              >
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color={colorMode === 'dark' ? 'stone.400' : 'stone.500'}
                >
                  /
                </Text>
              </HStack>
              {/* Subfolders */}
              {Array.from(root.children.values())
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((child) => (
                  <FolderRow
                    key={child.path}
                    node={child}
                    depth={1}
                    {...folderProps}
                  />
                ))}
              {/* Root-level shares (unfiled) */}
              {root.shares.map((share) => (
                <ShareItem
                  key={share.id}
                  share={share}
                  depth={1}
                  {...shareItemProps}
                />
              ))}
            </>
          )}
        </VStack>
      </Box>
    </>
  )
}
